import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { toast } from "sonner";

type UserRole = 'admin' | 'paid' | 'student' | 'free';

interface UserWithRole {
  id: string;
  email: string;
  created_at: string;
  role: UserRole | null;
  isBanned: boolean;
  banReason: string | null;
  usage: {
    totalTokens: number;
    totalCost: number;
    queryCount: number;
  };
}

interface ExportButtonProps {
  users: UserWithRole[];
}

export function ExportButton({ users }: ExportButtonProps) {
  const handleExport = () => {
    try {
      // Create CSV header
      const headers = [
        "Email",
        "Role",
        "Status",
        "Joined Date",
        "SuperCalc Queries",
        "Tokens Used",
        "Total Cost",
        "Ban Reason"
      ];

      // Create CSV rows
      const rows = users.map(user => [
        user.email,
        user.role || 'free',
        user.isBanned ? 'Banned' : 'Active',
        new Date(user.created_at).toLocaleDateString(),
        user.usage.queryCount.toString(),
        user.usage.totalTokens.toString(),
        `$${user.usage.totalCost.toFixed(4)}`,
        user.banReason || ''
      ]);

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success("Users data exported successfully");
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export data");
    }
  };

  return (
    <Button onClick={handleExport} variant="outline" className="gap-2">
      <Download className="h-4 w-4" />
      Export CSV
    </Button>
  );
}
